import React, { useState, useEffect, useRef, useMemo } from 'react';
import ORBIX_CHECK_CARDS from '../checksData';
import './HexCoreSection.css';

function CheckCardSection({ card }) {
  return (
    <article className="pm-check-card">
      <header className="pm-check-card__head">
        <span className="pm-check-card__dot" aria-hidden="true" />
        <h3 className="pm-check-card__title">{card.title}</h3>
      </header>
      <ol className="pm-check-card__list">
        {card.checks.map((chk, i) => (
          <li
            key={i}
            className={"pm-check-card__item" + (chk.conditional ? " pm-check-card__item--conditional" : "")}
          >
            <span className="pm-check-card__n">{String(i + 1).padStart(2, "0")}</span>
            <span className="pm-check-card__text" dangerouslySetInnerHTML={{ __html: chk.text }} />
          </li>
        ))}
      </ol>
    </article>
  );
}

function CheckRow({ cards, reverse = false, duration = 70 }) {
  return (
    <div className="pm-check-row" style={{ "--row-dur": `${duration}s` }}>
      <div className={`pm-check-row__belt${reverse ? " is-reverse" : ""}`}>
        {cards.map((c, i) => <CheckCardSection key={`a-${i}`} card={c} />)}
      </div>
      <div className={`pm-check-row__belt${reverse ? " is-reverse" : ""}`} aria-hidden="true">
        {cards.map((c, i) => <CheckCardSection key={`b-${i}`} card={c} />)}
      </div>
    </div>
  );
}

function ChecksCarousel({ singleRow = false }) {
  const all = ORBIX_CHECK_CARDS;
  if (singleRow) {
    return (
      <div className="pm-checks-carousel pm-checks-carousel--single">
        <CheckRow cards={all} reverse={false} duration={150} />
      </div>
    );
  }
  const rowA = all.filter((_, i) => i % 2 === 0);
  const rowB = all.filter((_, i) => i % 2 !== 0);
  return (
    <div className="pm-checks-carousel">
      <CheckRow cards={rowA} reverse={false} duration={90} />
      <CheckRow cards={rowB} reverse={true} duration={110} />
      <div className="pm-checks-carousel__edge pm-checks-carousel__edge--l" aria-hidden="true" />
      <div className="pm-checks-carousel__edge pm-checks-carousel__edge--r" aria-hidden="true" />
    </div>
  );
}

function HexCore() {
  const rootRef = useRef(null);
  const [live, setLive] = useState(false);

  const W = 1180, H = 820, cx = 590, cy = 410, R = 320;

  const corners = useMemo(() => {
    const defs = [
      { key: "specs",   deg: 0,   title: "Project Specifications &amp; NDT Procedures",  icon: "spec"      },
      { key: "pqr",     deg: 60,  title: "PQRs, Techniques &amp; Examination Reports",   icon: "clipboard" },
      { key: "imagery", deg: 120, title: "Inspection Imagery &amp; Data",                icon: "image"     },
      { key: "comms",   deg: 180, title: "User Communication &amp; Decisions",           icon: "chat"      },
      { key: "certs",   deg: 240, title: "Certifications",                               icon: "badge"     },
      { key: "extra",   deg: 300, title: "Any Additional Project Documentation",         icon: "folder"    },
    ];
    return defs.map(d => {
      const t = (d.deg * Math.PI) / 180;
      const isVertical = d.key === "specs" || d.key === "comms";
      const cardR = R + (isVertical ? 35 : 70);
      return {
        ...d,
        x: cx + R * Math.sin(t),
        y: cy - R * Math.cos(t),
        cardX: cx + cardR * Math.sin(t),
        cardY: cy - cardR * Math.cos(t),
      };
    });
  }, []);

  const revealOrder = [0, 1, 5, 2, 4, 3];
  const orderIndex = (i) => revealOrder.indexOf(i);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      entries => { entries.forEach(e => { if (e.intersectionRatio > 0.4) setLive(true); }); },
      { threshold: [0, 0.4, 0.6] }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const iconSvg = {
    spec: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M7 3h7l5 5v13H7z" /><path d="M14 3v5h5" /><path d="M10 12h6M10 16h6M10 8h2" />
      </svg>
    ),
    clipboard: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="5" y="4" width="14" height="17" rx="2" /><path d="M9 4V3h6v1" /><path d="M8.5 12.5l2 2 4-4" />
      </svg>
    ),
    image: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="5" width="18" height="14" rx="2" /><circle cx="8.5" cy="10" r="1.6" /><path d="M21 16l-5-4-4 3-2-1.5L3 18" />
      </svg>
    ),
    chat: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 5h16v11H9l-4 4z" /><path d="M8 9h8M8 12h5" />
      </svg>
    ),
    badge: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="9" r="5" /><path d="M9 13l-1.5 8L12 19l4.5 2L15 13" /><path d="M10 9l1.5 1.5L14 8" />
      </svg>
    ),
    folder: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 7a2 2 0 0 1 2-2h4l2 2.5h8a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      </svg>
    ),
  };

  const top = corners[0], ur = corners[1], lr = corners[2], bot = corners[3], ll = corners[4], ul = corners[5];
  const cwPath  = `M ${top.x} ${top.y} L ${ur.x} ${ur.y} L ${lr.x} ${lr.y} L ${bot.x} ${bot.y}`;
  const ccwPath = `M ${top.x} ${top.y} L ${ul.x} ${ul.y} L ${ll.x} ${ll.y} L ${bot.x} ${bot.y}`;

  return (
    <div ref={rootRef} className={`pm-hexcore${live ? " is-live" : ""}`}>
      <div className="pm-hexcore-stage">
        <svg className="pm-hexcore-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet">
          <defs>
            <linearGradient id="pmHxEdge" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2={W} y2={H}>
              <stop offset="0%" stopColor="#6366F1" />
              <stop offset="50%" stopColor="#8B5CF6" />
              <stop offset="100%" stopColor="#D946EF" />
            </linearGradient>
            <radialGradient id="pmHxCoreGlow" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0%" stopColor="#C4B5FD" stopOpacity="0.9" />
              <stop offset="40%" stopColor="#8B5CF6" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#6366F1" stopOpacity="0" />
            </radialGradient>
            <filter id="pmHxGlow" x="-60%" y="-60%" width="220%" height="220%">
              <feGaussianBlur stdDeviation="5" result="b" />
              <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
          </defs>

          <circle className="pm-hx-core-halo" cx={cx} cy={cy} r={230} fill="url(#pmHxCoreGlow)" />

          <polygon
            className="pm-hx-underlay"
            points={corners.map(c => `${c.x},${c.y}`).join(" ")}
            fill="rgba(139,92,246,0.03)"
            stroke="rgba(198,172,255,0.10)"
            strokeWidth="1"
          />

          {corners.map((c, i) => (
            <path
              key={`beam-${c.key}`}
              className="pm-hx-beam"
              d={`M ${c.x} ${c.y} L ${cx} ${cy}`}
              stroke="url(#pmHxEdge)"
              strokeWidth="2"
              fill="none"
              pathLength="100"
              style={{ "--beam-delay": `${1700 + orderIndex(i) * 110}ms` }}
            />
          ))}

          <path className="pm-hx-perim" d={cwPath} stroke="url(#pmHxEdge)" strokeWidth="3" fill="none" pathLength="100" filter="url(#pmHxGlow)" />
          <path className="pm-hx-perim" d={ccwPath} stroke="url(#pmHxEdge)" strokeWidth="3" fill="none" pathLength="100" filter="url(#pmHxGlow)" />

          {live && corners.map((c, i) => (
            <circle key={`pulse-${c.key}`} r="3.5" fill="#F0E7FF" filter="url(#pmHxGlow)" opacity="0">
              <animateMotion
                dur="2.4s"
                repeatCount="indefinite"
                begin={`${3600 + orderIndex(i) * 110}ms`}
                path={`M ${c.x} ${c.y} L ${cx} ${cy}`}
                keyPoints="0;1"
                keyTimes="0;1"
                calcMode="linear"
              />
              <animate
                attributeName="opacity"
                values="0;1;1;0"
                keyTimes="0;0.1;0.85;1"
                dur="2.4s"
                repeatCount="indefinite"
                begin={`${3600 + orderIndex(i) * 110}ms`}
              />
            </circle>
          ))}

          {corners.map((c, i) => (
            <g key={`vtx-${c.key}`} className="pm-hx-vtx" style={{ "--vtx-delay": `${500 + orderIndex(i) * 180}ms` }}>
              <circle cx={c.x} cy={c.y} r="7" fill="#0E0A1F" stroke="url(#pmHxEdge)" strokeWidth="2.5" />
              <circle cx={c.x} cy={c.y} r="3" fill="#F0E7FF" />
            </g>
          ))}
        </svg>

        {corners.map((c, i) => (
          <div
            key={`card-${c.key}`}
            className={`pm-hx-card pm-hx-card--${c.key}`}
            style={{
              left: `${(c.cardX / W) * 100}%`,
              top: `${(c.cardY / H) * 100}%`,
              "--card-delay": `${600 + orderIndex(i) * 180}ms`,
            }}
          >
            <span className="pm-hx-card__icon">{iconSvg[c.icon]}</span>
            <span className="pm-hx-card__label" dangerouslySetInnerHTML={{ __html: c.title }} />
          </div>
        ))}

        <div className="pm-hx-core" style={{ left: `${(cx / W) * 100}%`, top: `${(cy / H) * 100}%` }}>
          <div className="pm-hx-core__ring" />
          <div className="pm-hx-core__ring pm-hx-core__ring--2" />
          <div className="pm-hx-core__orb">
            <div className="pm-hx-core__label">Orbix<span>AI</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function HexCoreSection() {
  return (
    <section className="pm-scene-hexcore">
      <div className="pm-scene-inner">
        <div className="pm-hexcore-top">
          <div className="pm-hexcore-header pm-reveal">
            <h2>Expert-level AI review, running <em>24/7</em> across the entire inspection project.</h2>
            <p className="pm-lede">Every procedure. Every qualification. Every examination report. Every image. Every decision: reviewed continuously, like a senior Level III auditor.</p>
          </div>
          <div className="pm-hexcore-bg" aria-hidden="true">
            <ChecksCarousel singleRow={true} />
          </div>
        </div>
        <div className="pm-hexcore-mount">
          <HexCore />
        </div>
        <ul className="pm-hexcore-tiles" aria-label="What the AI quality team is connected to">
          <li><span className="pm-hexcore-tiles__core">Orbix · AI Quality Team</span></li>
          <li>Project Specifications &amp; NDT Procedures</li>
          <li>PQRs, Techniques &amp; Examination Reports</li>
          <li>Inspection Imagery &amp; Data</li>
          <li>User Communication &amp; Decisions</li>
          <li>Certifications</li>
          <li>Additional Project Documentation</li>
        </ul>
      </div>
    </section>
  );
}

export default HexCoreSection;
